import { createRoot, hydrateRoot } from 'react-dom/client';
import { isPrerenderedPathname } from '../utils/prerenderRoutes';
import {
  BOOT_RECOVERY_WINDOW_MS,
  clearBootReloadMarker,
  getBootFallbackMessage,
  isBootWindowActive,
  isChunkOrHydrationBootError,
  isReactSnap,
  logBootFailure,
  tryBootReloadOnce,
} from './bootRuntime';

const clearRootElement = (rootElement) => {
  if (!rootElement) {
    return;
  }

  while (rootElement.firstChild) {
    rootElement.removeChild(rootElement.firstChild);
  }
};

const showBootFallback = ({ rootElement, pathname, reload, error, source = 'boot' }) => {
  if (!rootElement) {
    return;
  }

  if (error) {
    logBootFailure({ error, pathname, source });
  }

  clearRootElement(rootElement);

  const wrapper = document.createElement('div');
  wrapper.setAttribute(
    'style',
    'min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;background:#f5f5f5;',
  );

  const card = document.createElement('div');
  card.setAttribute(
    'style',
    'max-width:440px;width:100%;background:#fff;border-radius:20px;padding:24px;box-shadow:0 16px 40px rgba(19,62,135,0.12);font-family:"Plus Jakarta Sans",Arial,sans-serif;color:#133e87;text-align:center;',
  );

  const title = document.createElement('h1');
  title.textContent = 'Page failed to load';
  title.setAttribute('style', 'margin:0 0 12px;font-size:1.5rem;line-height:1.2;');

  const message = document.createElement('p');
  message.textContent = getBootFallbackMessage(pathname);
  message.setAttribute('style', 'margin:0 0 20px;line-height:1.5;');

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Retry';
  button.setAttribute(
    'style',
    'border:0;border-radius:999px;padding:12px 20px;background:linear-gradient(135deg,#608bc1 0,#133e87 100%);color:#fff;font-weight:800;cursor:pointer;',
  );
  button.addEventListener('click', () => reload());

  card.appendChild(title);
  card.appendChild(message);
  card.appendChild(button);
  wrapper.appendChild(card);
  rootElement.appendChild(wrapper);
};

const getEventError = (event) => event?.reason || event?.error || event;

export const installBootFailureRecovery = ({
  rootElement,
  pathname,
  storage,
  reload,
  windowObject,
  recoveryWindowMs = BOOT_RECOVERY_WINDOW_MS,
}) => {
  if (!windowObject?.addEventListener || !windowObject?.removeEventListener) {
    return () => {};
  }

  let active = true;

  const cleanup = ({ preserveReloadMarker = false } = {}) => {
    if (!active) {
      return;
    }

    active = false;
    clearTimeout(timerId);
    windowObject.removeEventListener('error', handleBootError);
    windowObject.removeEventListener('unhandledrejection', handleBootError);
    if (!preserveReloadMarker) {
      clearBootReloadMarker({ pathname, storage });
    }
  };

  const handleBootError = (event) => {
    if (!active) {
      return;
    }

    const error = getEventError(event);
    if (!isBootWindowActive() || !isChunkOrHydrationBootError(error)) {
      return;
    }

    if (event?.preventDefault) {
      event.preventDefault();
    }

    logBootFailure({ error, pathname, source: 'global-listener-reload' });

    if (tryBootReloadOnce({ pathname, storage, reload })) {
      cleanup({ preserveReloadMarker: true });
      return;
    }

    cleanup();
    showBootFallback({ rootElement, pathname, reload, error, source: 'global-listener' });
  };

  const timerId = windowObject.setTimeout
    ? windowObject.setTimeout(cleanup, recoveryWindowMs)
    : setTimeout(cleanup, recoveryWindowMs);

  windowObject.addEventListener('error', handleBootError);
  windowObject.addEventListener('unhandledrejection', handleBootError);

  return cleanup;
};

export const mountApp = ({
  rootElement,
  app,
  pathname,
  createRootImpl = createRoot,
  hydrateRootImpl = hydrateRoot,
}) => {
  if (!rootElement) {
    return { mode: 'noop' };
  }

  if (isPrerenderedPathname(pathname) && rootElement.hasChildNodes()) {
    hydrateRootImpl(rootElement, app);
    return { mode: 'hydrate' };
  }

  clearRootElement(rootElement);
  const root = createRootImpl(rootElement);
  root.render(app);
  return { mode: 'createRoot', root };
};

export const bootstrapApp = ({
  rootElement,
  app,
  pathname = typeof window !== 'undefined' ? window.location.pathname : '/',
  storage = typeof window !== 'undefined' ? window.sessionStorage : null,
  reload = typeof window !== 'undefined' ? () => window.location.reload() : () => {},
  windowObject = typeof window !== 'undefined' ? window : null,
  createRootImpl = createRoot,
  hydrateRootImpl = hydrateRoot,
}) => {
  if (!rootElement) {
    return { mode: 'noop', cleanup: () => {} };
  }

  const cleanup = isReactSnap(windowObject)
    ? () => {}
    : installBootFailureRecovery({
        rootElement,
        pathname,
        storage,
        reload,
        windowObject,
      });

  try {
    const result = mountApp({
      rootElement,
      app,
      pathname,
      createRootImpl,
      hydrateRootImpl,
    });
    return { ...result, cleanup };
  } catch (error) {
    const isBootRecoverable = isBootWindowActive() && isChunkOrHydrationBootError(error);

    if (isBootRecoverable) {
      logBootFailure({ error, pathname, source: 'bootstrap-reload' });
    }

    if (isBootRecoverable && tryBootReloadOnce({ pathname, storage, reload })) {
      cleanup({ preserveReloadMarker: true });
      return { mode: 'reload' };
    }

    cleanup();
    showBootFallback({ rootElement, pathname, reload, error, source: 'bootstrap' });
    return { mode: 'fallback' };
  }
};
